<script setup>
/**
 * ImmersiveCatalog
 * Full-screen immersive product browser. Renders a THREE-pane track
 * (prev / current / next) that follows the finger 1:1 (Reels/TikTok model) and
 * walks the flat ordered feed with swipe / wheel / keyboard / on-screen
 * chevrons — every input shares the same settle spring. Dark navy by default
 * with a light toggle (brand palette), plus an ambient blurred backdrop of the
 * current product. Pure presentation + navigation: the host view owns the item
 * list and current index and acts on next/prev/jump/close.
 */
import { computed, ref, nextTick, onMounted, onUnmounted, watch } from 'vue'
import {
  useImmersiveSwipe,
  prefersReducedMotion,
} from '../../../../core/composables/useImmersiveSwipe.js'
import { useImmersiveTheme } from '../../../../core/composables/useImmersiveTheme.js'
import { useBackButtonClose } from '../../../../core/composables/useBackButtonClose.js'
import { useCartStore } from '../../../../app/store/cart.js'
import { useCatalogStore } from '../../../../app/store/catalog.js'
import {
  getNextStepHint,
  computeCumulativeMadregotApplication,
} from '../../../../core/utils/promoCalc.js'
import { hapticTap, hapticSuccess } from '../../../../core/utils/haptics.js'
import { playAchievementChime, primeAudio } from '../../../../core/utils/sounds.js'
import ImmersiveCorners from './ImmersiveCorners.vue'
import ImmersivePane from './ImmersivePane.vue'
import ImmersivePromoPane from './ImmersivePromoPane.vue'
import ImmersivePromoItemsModal from './ImmersivePromoItemsModal.vue'
import ImmersiveBackdrop from './ImmersiveBackdrop.vue'
import ImmersiveCelebration from './ImmersiveCelebration.vue'
import ImmersiveOnboarding from './ImmersiveOnboarding.vue'

const props = defineProps({
  item: { type: Object, default: null },
  prevItem: { type: Object, default: null },
  nextItem: { type: Object, default: null },
  index: { type: Number, default: 0 }, // 0-based position in the feed
  total: { type: Number, default: 0 },
  departments: { type: Array, default: () => [] }, // [{ id, name, count, firstIndex }]
  // { prevIndex, nextIndex } — first item of the prev/next sub-category within
  // the current department (-1 = none → the double-chevron button hides).
  subJump: { type: Object, default: null },
  // Subs of the current department for the selector sheet:
  // [{ id, name, firstIndex, count }] (id null = the "no sub" group).
  subs: { type: Array, default: () => [] },
  showPrices: { type: Boolean, default: true },
  showCart: { type: Boolean, default: false }, // host decides (role-gated like the app shell)
})

const emit = defineEmits(['next', 'prev', 'close', 'jump', 'jump-index'])

// Sub-category jumps (double chevrons + selector sheet) land with a one-pane
// "scroll into place" settle: adjacent targets get the true commit animation;
// farther ones swap first, then settleFrom slides the target in from the
// travel direction. Reduced motion keeps the plain instant swap.
function jumpToSubIndex(idx) {
  if (typeof idx !== 'number' || idx < 0 || idx === props.index) return
  hapticTap()
  if (Math.abs(idx - props.index) === 1) {
    commit(idx > props.index ? 'next' : 'prev')
    return
  }
  const dir = idx > props.index ? 'next' : 'prev'
  emit('jump-index', idx)
  nextTick(() => settleFrom(dir))
}

const { theme, isSwitching } = useImmersiveTheme()

// Virtual promotions department id — its grid tile gets the promo-red identity.
const PROMO_DEPARTMENT_ID = useCatalogStore().PROMO_DEPARTMENT_ID

const currentDept = computed(
  () => props.departments.find((d) => d.id === props.item?.department) || null
)

// Ambient backdrop follows the COMMITTED item only (never the live drag).
const backdropImageUrl = computed(() => {
  const img = props.item?.image
  return typeof img === 'string' && img.trim().length > 0 ? img : null
})

// ── Swipe track (prev / current / next) ─────────────────────────
// Keyed by FEED INDEX (not makat — a makat can repeat when the promo virtual
// department and a real department contain the same item). After a commit the
// neighbor pane keeps its key, so Vue reuses its DOM: the incoming pane simply
// "becomes" current with its image already decoded — no flash.
const panes = computed(() => {
  const list = []
  if (props.prevItem) list.push({ key: props.index - 1, item: props.prevItem, pos: 'prev' })
  if (props.item) list.push({ key: props.index, item: props.item, pos: 'current' })
  if (props.nextItem) list.push({ key: props.index + 1, item: props.nextItem, pos: 'next' })
  return list
})

const surfaceRef = ref(null)

// A panel that genuinely overflows owns its vertical touches/wheel (scrolling
// beats navigation there); anywhere else the gesture engine takes over.
// Covers BOTH pane flavors: the item pane's .imm-panel and the promo pane's
// .imm-pp-panel (both are content-sized and only overflow on extreme viewports).
function ownsOwnScroll(target) {
  const panel = target?.closest?.('.imm-panel, .imm-pp-panel')
  return !!panel && panel.scrollHeight > panel.clientHeight + 1
}

const {
  dragOffset,
  isDragging,
  isSettling,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTouchCancel,
  commit,
  nudge,
  settleFrom,
  resetSwipe,
} = useImmersiveSwipe({
  getPaneHeight: () => surfaceRef.value?.clientHeight || window.innerHeight,
  canNext: () => props.index < props.total - 1,
  canPrev: () => props.index > 0,
  shouldIgnore: ownsOwnScroll,
  onDragStart: () => dismissHint(),
  onBeforeCommit: () => hapticTap(),
  onCommit: (dir) => {
    dismissHint()
    emit(dir === 'next' ? 'next' : 'prev')
  },
})

const trackStyle = computed(() => ({
  transform: `translate3d(0, ${dragOffset.value}px, 0)`,
}))

// Edge feedback for the host (kept for API compatibility) — now a spring nudge.
function bounce(dir) {
  nudge(dir)
}
defineExpose({ bounce })

// Mouse wheel (PC/trackpad): one animated commit per gesture. The cooldown
// swallows trackpad momentum tails so a single flick can't chain-skip items.
let wheelUntil = 0
function onWheel(e) {
  if (Math.abs(e.deltaY) < 8) return
  if (ownsOwnScroll(e.target)) return
  const now = Date.now()
  if (now < wheelUntil) return
  const dir = e.deltaY > 0 ? 'next' : 'prev'
  wheelUntil = now + (commit(dir) ? 520 : 380)
}

// Keyboard (PC)
function isEditableTarget(el) {
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
}

function onKeydown(e) {
  // Any real key gesture keeps the chime's audio context unlocked (PC flows).
  primeAudio()
  // The app cart drawer is modal ABOVE the immersive — while it's open it owns
  // every key (it closes itself on Escape); react to nothing here. This
  // listener runs in the capture phase so the guard still sees the drawer as
  // open (its own document-level Escape handler hasn't closed it yet).
  if (cartStore.isCartOpen) return
  // Onboarding swallows navigation; Escape/Enter dismiss it.
  if (showOnboarding.value) {
    if (e.key === 'Escape' || e.key === 'Enter') dismissOnboarding()
    return
  }
  // Escape closes the topmost layer first, otherwise the whole overlay.
  if (e.key === 'Escape') {
    if (showPromoModal.value) closePromoItems()
    else if (showSubSheet.value) closeSubSheet()
    else if (showDeptSheet.value) closeDeptSheet()
    else emit('close')
    return
  }
  // While a sheet/modal is open, swallow navigation keys.
  if (showDeptSheet.value || showSubSheet.value || showPromoModal.value) return
  // Don't hijack arrows/page keys while the user is typing in the quantity field.
  if (isEditableTarget(e.target)) return
  if (e.key === 'ArrowDown' || e.key === 'PageDown') {
    e.preventDefault()
    commit('next')
  } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
    e.preventDefault()
    commit('prev')
  }
}

// ── Celebration (discount tier / promo step reached) ────────────
// Pop + hold at center, then the badge FLIES into the checkpoint dot it just
// unlocked on the current pane's steps rail and is absorbed there (dot pulse).
// When no rail dot is on screen (e.g. multi-item promo panes) it falls back to
// the original center fade-out.
const celebration = ref({ show: false, kind: 'discount', label: '' })
// The rail dot the badge flies into (DOM element; queried at fly time).
const celebrationFlyEl = ref(null)
let celebrationTimer = null
let celebrationFlyTimer = null
let celebrationAbsorbTimer = null

const CELEBRATE_HOLD_MS = 950 // pop (0.75s) + brief hold before the flight
const CELEBRATE_FALLBACK_MS = 1500 // total on-screen time when nothing to fly to

function onCelebrate({ kind, label }) {
  if (celebrationTimer) clearTimeout(celebrationTimer)
  if (celebrationFlyTimer) clearTimeout(celebrationFlyTimer)
  celebrationFlyEl.value = null
  // Hide → next tick show: re-creates the badge element so the pop/spark
  // keyframes restart even when a celebration is already on screen.
  celebration.value = { ...celebration.value, show: false }
  nextTick(() => {
    celebration.value = { show: true, kind, label }
    hapticSuccess()
    playAchievementChime()
    if (prefersReducedMotion()) {
      celebrationTimer = setTimeout(() => {
        celebration.value = { ...celebration.value, show: false }
      }, 900)
      return
    }
    celebrationFlyTimer = setTimeout(() => {
      // The tier that just applied is the rail's `.active` checkpoint — the
      // ladder re-rendered when the price recomputed, long before this timer.
      const dot = surfaceRef.value?.querySelector(
        '.imm-track-pane.pane-current .imm-disc-cp.active .imm-disc-dot'
      )
      if (dot) {
        celebrationFlyEl.value = dot // starts the fly; hide happens on fly-done
      } else {
        celebrationTimer = setTimeout(() => {
          celebration.value = { ...celebration.value, show: false }
        }, CELEBRATE_FALLBACK_MS - CELEBRATE_HOLD_MS)
      }
    }, CELEBRATE_HOLD_MS)
  })
}

function onCelebrateFlyDone() {
  // The badge just landed — pulse the dot so it visibly "absorbs" it. The
  // class is added straight on the rail's DOM node (transient juice; the
  // matching animation lives in ImmersiveDiscountBar's scoped CSS, which still
  // applies because the scope attribute is on the element itself).
  const dot = celebrationFlyEl.value
  if (dot) {
    dot.classList.remove('imm-disc-dot-absorb')
    void dot.offsetWidth // restart the animation on back-to-back celebrations
    dot.classList.add('imm-disc-dot-absorb')
    if (celebrationAbsorbTimer) clearTimeout(celebrationAbsorbTimer)
    celebrationAbsorbTimer = setTimeout(() => dot.classList.remove('imm-disc-dot-absorb'), 700)
  }
  celebrationFlyEl.value = null
  celebration.value = { ...celebration.value, show: false }
}

const celebrationAria = computed(() =>
  celebration.value.kind === 'promo'
    ? `הגעת למחיר מבצע ${celebration.value.label}`
    : `הגעת להנחת כמות ${celebration.value.label}`
)

// ── Feed progress ────────────────────────────────────────────────
const progressRatio = computed(() =>
  props.total > 0 ? Math.min(1, (props.index + 1) / props.total) : 0
)

// ── Multi-item promo: items sub-modal ───────────────────────────
const showPromoModal = ref(false)
const promoForModal = ref(null)

function openPromoItems(promo) {
  promoForModal.value = promo
  showPromoModal.value = true
}

function closePromoItems() {
  showPromoModal.value = false
  maybeShowPromoHintToast()
}
useBackButtonClose(showPromoModal, closePromoItems)

// ── Next-step hint toast ─────────────────────────────────────────
// NO pane carries an in-layout hint line (it grew the panel after adds and
// pushed it into overflow). Instead the overlay shows a transient top toast:
// item panes emit 'hint' on qty changes short of the next step, and the promo
// items modal fires one on close. No layout impact, auto-dismisses.
const hintToast = ref({ visible: false, text: '', gold: false })
let hintToastTimer = null

function showHintToast(text, gold) {
  if (hintToastTimer) clearTimeout(hintToastTimer)
  // Already on screen → update the text in place (no blink); the enter
  // transition only plays from hidden. Every call restarts the clock, so rapid
  // stepper taps read as one live-updating counter that fades after the last.
  hintToast.value = { visible: true, text, gold }
  hintToastTimer = setTimeout(() => {
    hintToast.value = { ...hintToast.value, visible: false }
  }, 2600)
}

// Item-pane hints. Suppressed while the cart drawer covers the overlay and
// while the promo items modal is open (it shows its own live hint strip).
function onHintToast({ text, gold }) {
  if (cartStore.isCartOpen || showPromoModal.value) return
  showHintToast(text, gold)
}

function maybeShowPromoHintToast() {
  const promo = promoForModal.value
  if (!promo || !props.showPrices) return
  const cartLinesByMakat = {}
  for (const line of cartStore.linesArray) {
    cartLinesByMakat[line.makat] = { qty: line.qty, addedAt: line.addedAt }
  }
  const app = computeCumulativeMadregotApplication({ promo, cartLinesByMakat })
  if (!app.totalQty) return
  const hint = getNextStepHint(promo, app.totalQty)
  if (!hint) return
  showHintToast(
    `עוד ${hint.unitsNeeded} יח' למחיר ₪${hint.nextStep.price.toFixed(2)} ליחידה`,
    promo.first_order === true
  )
}

// ── Department jump sheet ────────────────────────────────────────
const showDeptSheet = ref(false)
function openDeptSheet() {
  showDeptSheet.value = true
}
function closeDeptSheet() {
  showDeptSheet.value = false
}
function selectDept(deptId) {
  closeDeptSheet()
  emit('jump', deptId)
}
useBackButtonClose(showDeptSheet, closeDeptSheet)

// ── Sub-category selector sheet (same chrome as the department sheet) ──
const showSubSheet = ref(false)
const currentSubId = computed(() => props.item?.subDepartment ?? null)
function openSubSheet() {
  if (!props.subs || props.subs.length <= 1) return
  showSubSheet.value = true
}
function closeSubSheet() {
  showSubSheet.value = false
}
function selectSub(firstIndex) {
  closeSubSheet()
  jumpToSubIndex(firstIndex)
}
useBackButtonClose(showSubSheet, closeSubSheet)

// ── Floating cart (bottom-corner FAB → the app cart drawer) ─────
// The drawer itself lives in AppShellLayout; opening goes through the cart
// store (isCartOpen), which the shell mirrors into the drawer's v-model. The
// drawer elevates itself above this overlay on the immersive route, so closing
// it simply reveals the immersive exactly as it was.
const cartStore = useCartStore()
const cartSkuCount = computed(() => cartStore.uniqueCount)
const cartBadgeText = computed(() => (cartSkuCount.value > 99 ? '99+' : String(cartSkuCount.value)))

function openCart() {
  hapticTap()
  cartStore.setCartOpen(true)
}

// Add-to-cart feedback: icon bump + badge pop + expanding pulse ring.
// Alternating a/b classes restart the bump keyframes on consecutive adds
// (same trick as the quick-add qty pulse); the keyed ring re-creates per add.
const cartBump = ref('')
const cartPulseKey = ref(0)
watch(
  () => cartStore.itemsCount,
  (nv, ov) => {
    if (nv > ov) {
      cartBump.value = cartBump.value === 'cart-bump-a' ? 'cart-bump-b' : 'cart-bump-a'
      cartPulseKey.value++
    }
  }
)

// The cart drawer clears the body scroll lock when it closes — re-assert the
// immersive lock (set on mount below) so the page behind stays frozen.
watch(
  () => cartStore.isCartOpen,
  (open) => {
    if (!open) {
      nextTick(() => {
        document.body.style.overflow = 'hidden'
      })
    }
  }
)

// ── Onboarding (every entry, until the user opts out) ───────────
const ONBOARDING_KEY = 'gofood:immersiveOnboardingHide'
const showOnboarding = ref(false)
// Host-owned so Escape/Enter/back-button dismissals also see the toggle state.
const onboardingDontShowAgain = ref(false)

function hasHiddenOnboarding() {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === '1'
  } catch {
    return false
  }
}

function dismissOnboarding() {
  showOnboarding.value = false
  if (!onboardingDontShowAgain.value) return
  try {
    localStorage.setItem(ONBOARDING_KEY, '1')
  } catch {
    // best-effort persistence
  }
}
useBackButtonClose(showOnboarding, dismissOnboarding)

// ── Intro splash + first-use swipe hint (one-shot per mount) ─────
const showIntro = ref(false)
const showHint = ref(false)
let introTimeout = null
let hintTimeout = null

function dismissHint() {
  if (!showHint.value) return
  if (hintTimeout) clearTimeout(hintTimeout)
  showHint.value = false
}

// Any navigation dismisses the hint
watch(
  () => props.item,
  () => dismissHint()
)

function startIntroAndHint() {
  if (prefersReducedMotion()) return
  showIntro.value = true
  introTimeout = setTimeout(() => {
    showIntro.value = false
  }, 700)
  showHint.value = true
  hintTimeout = setTimeout(() => {
    showHint.value = false
  }, 2600)
}

onMounted(() => {
  document.body.style.overflow = 'hidden'
  // Capture phase: must run BEFORE the cart drawer's document-level Escape
  // handler so the cart-open guard above reads the pre-close state.
  window.addEventListener('keydown', onKeydown, { capture: true })
  // Unlock the chime's audio context from real gestures: the celebration fires
  // from watchers (outside the tap's call stack), where browsers would refuse
  // to START audio — so every tap keeps the context primed ahead of time.
  window.addEventListener('pointerdown', primeAudio, { capture: true, passive: true })

  // Show the full onboarding on every entry until the user opts out via the
  // "don't show again" toggle; opted-out entries get the brief intro/hint.
  if (!hasHiddenOnboarding()) {
    showOnboarding.value = true
  } else {
    startIntroAndHint()
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
  window.removeEventListener('keydown', onKeydown, { capture: true })
  window.removeEventListener('pointerdown', primeAudio, { capture: true })
  resetSwipe()
  if (celebrationTimer) clearTimeout(celebrationTimer)
  if (celebrationFlyTimer) clearTimeout(celebrationFlyTimer)
  if (celebrationAbsorbTimer) clearTimeout(celebrationAbsorbTimer)
  if (introTimeout) clearTimeout(introTimeout)
  if (hintTimeout) clearTimeout(hintTimeout)
  if (hintToastTimer) clearTimeout(hintToastTimer)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="imm-overlay" appear>
      <div
        class="immersive-overlay"
        :class="{ 'theme-light': theme === 'light', 'theme-switching': isSwitching }"
        dir="rtl"
      >
        <!-- Ambient blurred backdrop (committed item only — calm under the finger) -->
        <ImmersiveBackdrop :image-url="backdropImageUrl" />

        <div class="immersive-frame">
          <ImmersiveCorners @close="emit('close')" />

          <!-- Top bar: department pill + position counter -->
          <div class="imm-topbar">
            <button
              v-if="departments.length"
              type="button"
              class="imm-dept-pill"
              @click="openDeptSheet"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
              </svg>
              <span class="imm-dept-name">{{ currentDept?.name || 'מחלקות' }}</span>
              <svg
                class="imm-dept-chev"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <span v-if="total" class="imm-counter">{{ index + 1 }} / {{ total }}</span>
          </div>

          <!-- Feed position — thin progress rail (fills from the physical right, RTL) -->
          <div class="imm-progress" aria-hidden="true">
            <div class="imm-progress-fill" :style="{ transform: `scaleX(${progressRatio})` }"></div>
          </div>

          <!-- Item surface: the finger-follow track with prev/current/next panes -->
          <div
            ref="surfaceRef"
            class="immersive-surface"
            @touchstart.passive="onTouchStart"
            @touchmove="onTouchMove"
            @touchend.passive="onTouchEnd"
            @touchcancel.passive="onTouchCancel"
            @wheel.passive="onWheel"
          >
            <div
              class="imm-track"
              :class="{ 'is-settling': isSettling, 'is-dragging': isDragging }"
              :style="trackStyle"
            >
              <template v-for="p in panes" :key="p.key">
                <!-- Multi-item promotion card pane -->
                <ImmersivePromoPane
                  v-if="p.item.__promoCard"
                  class="imm-track-pane"
                  :class="`pane-${p.pos}`"
                  :promo="p.item.promo"
                  :show-prices="showPrices"
                  :is-current="p.pos === 'current'"
                  @open-items="openPromoItems"
                  @celebrate="onCelebrate"
                />
                <!-- Regular item pane -->
                <ImmersivePane
                  v-else
                  class="imm-track-pane"
                  :class="`pane-${p.pos}`"
                  :item="p.item"
                  :show-prices="showPrices"
                  :is-current="p.pos === 'current'"
                  @celebrate="onCelebrate"
                  @hint="onHintToast"
                />
              </template>
            </div>
          </div>

          <!-- Navigation chevrons: double = prev/next SUB-CATEGORY (hidden at
               the department's first/last sub), single = prev/next item. -->
          <div class="imm-nav">
            <button
              type="button"
              class="imm-nav-btn imm-nav-btn-sub"
              :class="{ 'is-hidden': !subJump || subJump.prevIndex < 0 }"
              aria-label="קפיצה לתת־הקטגוריה הקודמת"
              @click="jumpToSubIndex(subJump?.prevIndex ?? -1)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="18 11 12 5 6 11" />
                <polyline points="18 18 12 12 6 18" />
              </svg>
            </button>
            <button
              type="button"
              class="imm-nav-btn"
              aria-label="הפריט הקודם"
              @click="commit('prev')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>
            <button
              type="button"
              class="imm-nav-btn imm-nav-btn-sub"
              :class="{ 'is-hidden': !subs || subs.length <= 1 }"
              aria-label="בחירת תת־קטגוריה"
              @click="openSubSheet"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polygon points="12 2 2 7 12 12 22 7 12 2" />
                <polyline points="2 12 12 17 22 12" />
                <polyline points="2 17 12 22 22 17" />
              </svg>
            </button>
            <button
              type="button"
              class="imm-nav-btn"
              aria-label="הפריט הבא"
              @click="commit('next')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <button
              type="button"
              class="imm-nav-btn imm-nav-btn-sub"
              :class="{ 'is-hidden': !subJump || subJump.nextIndex < 0 }"
              aria-label="קפיצה לתת־הקטגוריה הבאה"
              @click="jumpToSubIndex(subJump?.nextIndex ?? -1)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="6 6 12 12 18 6" />
                <polyline points="6 13 12 19 18 13" />
              </svg>
            </button>
          </div>

          <!-- Floating cart (bottom inline-start corner = physical bottom-right in RTL) -->
          <button
            v-if="showCart"
            type="button"
            class="imm-cart-btn"
            :class="cartBump"
            aria-label="פתיחת עגלת הקניות"
            @click="openCart"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
            </svg>
            <Transition name="imm-cart-badge">
              <span v-if="cartSkuCount > 0" class="imm-cart-badge">{{ cartBadgeText }}</span>
            </Transition>
            <span
              v-if="cartPulseKey"
              :key="cartPulseKey"
              class="imm-cart-ring"
              aria-hidden="true"
            ></span>
          </button>

          <!-- Next-step hint toast (promo panes; fired when the items modal closes) -->
          <Transition name="imm-toast">
            <div
              v-if="hintToast.visible"
              class="imm-toast"
              :class="{ 'is-gold': hintToast.gold }"
              role="status"
            >
              {{ hintToast.text }}
            </div>
          </Transition>

          <!-- Intro splash -->
          <Transition name="imm-intro">
            <div v-if="showIntro" class="imm-intro" aria-hidden="true">
              <span class="imm-intro-text">מצב קטלוג אימרסיבי</span>
            </div>
          </Transition>

          <!-- First-use swipe hint -->
          <Transition name="imm-hint">
            <div v-if="showHint" class="imm-swipe-hint" aria-hidden="true">
              <svg
                class="imm-hint-chevron"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
              <span class="imm-hint-text">החליקו / גללו למעבר בין פריטים</span>
            </div>
          </Transition>

          <!-- Discount-tier / promo-step celebration -->
          <ImmersiveCelebration
            :show="celebration.show"
            :kind="celebration.kind"
            :label="celebration.label"
            :aria-label="celebrationAria"
            :fly-target-el="celebrationFlyEl"
            @fly-done="onCelebrateFlyDone"
          />
        </div>

        <!-- Department jump sheet -->
        <Transition name="imm-sheet">
          <div v-if="showDeptSheet" class="imm-sheet-backdrop" @click.self="closeDeptSheet">
            <div class="imm-sheet">
              <div class="imm-sheet-header">
                <span>מעבר למחלקה</span>
                <button
                  type="button"
                  class="imm-sheet-close"
                  aria-label="סגירה"
                  @click="closeDeptSheet"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="imm-sheet-list">
                <button
                  v-for="dept in departments"
                  :key="dept.id"
                  type="button"
                  class="imm-sheet-item"
                  :class="{
                    active: dept.id === currentDept?.id,
                    'is-promo': dept.id === PROMO_DEPARTMENT_ID,
                  }"
                  @click="selectDept(dept.id)"
                >
                  <!-- Current-category badge (grid tiles only; hidden on the mobile list) -->
                  <span
                    v-if="dept.id === currentDept?.id"
                    class="imm-sheet-item-check"
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span class="imm-sheet-item-name">{{ dept.name }}</span>
                  <span class="imm-sheet-item-count">{{ dept.count }}</span>
                </button>
              </div>
            </div>
          </div>
        </Transition>

        <!-- Sub-category selector sheet — identical chrome to the department
             sheet (mobile bottom list / tablet takeover / PC glass panel). -->
        <Transition name="imm-sheet">
          <div v-if="showSubSheet" class="imm-sheet-backdrop" @click.self="closeSubSheet">
            <div class="imm-sheet">
              <div class="imm-sheet-header">
                <span>מעבר לתת־קטגוריה</span>
                <button
                  type="button"
                  class="imm-sheet-close"
                  aria-label="סגירה"
                  @click="closeSubSheet"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="imm-sheet-list">
                <button
                  v-for="sub in subs"
                  :key="sub.id ?? '__no-sub__'"
                  type="button"
                  class="imm-sheet-item"
                  :class="{ active: (sub.id ?? null) === currentSubId }"
                  @click="selectSub(sub.firstIndex)"
                >
                  <span
                    v-if="(sub.id ?? null) === currentSubId"
                    class="imm-sheet-item-check"
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span class="imm-sheet-item-name">{{ sub.name }}</span>
                  <span class="imm-sheet-item-count">{{ sub.count }}</span>
                </button>
              </div>
            </div>
          </div>
        </Transition>

        <!-- Multi-item promo: per-item quantities sub-modal -->
        <Transition name="imm-pm">
          <ImmersivePromoItemsModal
            v-if="showPromoModal && promoForModal"
            :promo="promoForModal"
            :show-prices="showPrices"
            @close="closePromoItems"
          />
        </Transition>

        <!-- Onboarding (full-screen, every entry until opted out) -->
        <Transition name="imm-onb">
          <ImmersiveOnboarding
            v-if="showOnboarding"
            v-model:dont-show-again="onboardingDontShowAgain"
            @dismiss="dismissOnboarding"
          />
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.immersive-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background-color: var(--imm-bg-base, #0a0f1a);
  color: var(--imm-text, #ffffff);

  /* ── Responsive size tokens (mobile base; the tablet & PC media blocks below
     override them). Declared on the overlay root so they cascade to BOTH the
     pane regions AND the frame-level chrome (corners + nav). One source of
     truth — sub-components read these vars instead of hardcoding sizes.
     Controls are deliberately LARGE: they are the star of this screen. ── */
  --imm-price-size: 2.3rem;
  --imm-orig-size: 0.95rem;
  --imm-name-size: 1.15rem;
  --imm-ident-size: 0.8rem;
  --imm-total-size: 0.9rem;
  --imm-step-size: 64px;
  --imm-step-font: 1.7rem;
  --imm-qty-w: 76px;
  --imm-trash-size: 52px;
  --imm-img-max: 40vh;
  --imm-disc-w: 64px;
  --imm-disc-gutter: 72px;
  --imm-disc-dot: 12px;
  --imm-disc-qty-font: 0.6rem;
  --imm-disc-price-font: 0.72rem;
  --imm-disc-caption: 0.62rem;
  --imm-nav-size: 44px;
  --imm-corner-size: 44px;
  --imm-corner-reserve: 124px;
  --imm-dept-size: 0.82rem;

  height: 100dvh;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
}

/* Radial brand-navy depth layers (gradients can't transition, so cross-fade).
   They paint ABOVE the ambient backdrop (z -1) — glow over ambience. */
.immersive-overlay::before,
.immersive-overlay::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.immersive-overlay::before {
  background: radial-gradient(
    120% 80% at 50% 0%,
    rgba(59, 130, 246, 0.16) 0%,
    rgba(10, 15, 26, 0) 55%
  );
  opacity: 1;
}

.immersive-overlay::after {
  background: radial-gradient(
    120% 80% at 50% 0%,
    rgba(40, 53, 72, 0.12) 0%,
    rgba(244, 247, 251, 0) 55%
  );
  opacity: 0;
}

.immersive-overlay.theme-light::before {
  opacity: 0;
}

.immersive-overlay.theme-light::after {
  opacity: 1;
}

/* ── Light theme: the single source of truth for every immersive color. Dark
   defines none (each consumer uses var(--imm-x, <dark fallback>)). ── */
.immersive-overlay.theme-light {
  --imm-bg-base: #f4f7fb;
  --imm-text: #0f172a;
  --imm-text-soft: rgba(15, 23, 42, 0.78);
  --imm-text-muted: rgba(15, 23, 42, 0.65);
  --imm-text-faint: rgba(15, 23, 42, 0.5);
  --imm-name-shadow: 0 1px 6px rgba(255, 255, 255, 0.85);
  --imm-placeholder: rgba(15, 23, 42, 0.2);
  --imm-glass-bg: rgba(255, 255, 255, 0.7);
  --imm-glass-border: rgba(15, 23, 42, 0.14);
  --imm-glass-active: rgba(15, 23, 42, 0.12);
  --imm-icon: #0f172a;
  --imm-field-bg: rgba(255, 255, 255, 0.85);
  --imm-field-border: rgba(15, 23, 42, 0.18);
  --imm-step-active-bg: rgba(59, 130, 246, 0.18);
  --imm-accent: #2563eb;
  --imm-success: #15803d;
  --imm-warning: #b45309;
  --imm-error: #dc2626;
  --imm-pill-bg: rgba(255, 255, 255, 0.78);
  --imm-pill-border: rgba(15, 23, 42, 0.1);
  /* Ambient backdrop: lighter tint + white-weighted scrim on the light theme. */
  --imm-ambient-opacity: 0.16;
  --imm-ambient-scrim: linear-gradient(
    180deg,
    rgba(244, 247, 251, 0.32) 0%,
    rgba(244, 247, 251, 0.62) 55%,
    rgba(244, 247, 251, 0.86) 100%
  );
  /* Celebration badge shadow is softer over the light overlay. */
  --imm-celebrate-shadow: rgba(15, 23, 42, 0.18);
  /* Clean flat hero on light — no drop shadow under the product image. */
  --imm-img-shadow: none;
  /* No neon text glow on light surfaces. */
  --imm-total-glow: none;
}

/* Cross-fade window after a toggle. */
.immersive-overlay.theme-switching,
.immersive-overlay.theme-switching :deep(*) {
  transition:
    background-color 0.3s ease,
    background 0.3s ease,
    color 0.3s ease,
    border-color 0.3s ease,
    box-shadow 0.3s ease,
    filter 0.3s ease,
    opacity 0.3s ease,
    transform 0.3s ease;
}

.immersive-frame {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100dvh;
  /* Centered column; widens per breakpoint (tablet/PC blocks below) so the
     experience fills the container instead of floating as a phone-width strip. */
  max-width: 540px;
  margin: 0 auto;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* ── Top bar ── */
.imm-topbar {
  /* Auto height (not 10%) so the hero image gets the maximum vertical share. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  /* dept-pill + counter on the physical RIGHT (inline-start in RTL); reserve the
     physical LEFT (inline-end) for the close + theme corner buttons. */
  justify-content: flex-start;
  padding-block: 8px 6px;
  padding-inline: 16px var(--imm-corner-reserve, 124px);
  min-height: 56px;
  overflow: hidden;
}

.imm-dept-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 60vw;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid var(--imm-pill-border, rgba(255, 255, 255, 0.14));
  background: var(--imm-pill-bg, rgba(15, 23, 42, 0.55));
  color: var(--imm-text, #ffffff);
  font-size: var(--imm-dept-size, 0.82rem);
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transition:
    background 0.15s ease,
    transform 0.1s ease;
  -webkit-tap-highlight-color: transparent;
}

.imm-dept-pill:active {
  transform: scale(0.96);
}

.imm-dept-pill svg {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.imm-dept-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.imm-dept-chev {
  opacity: 0.7;
}

.imm-counter {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.6));
  font-variant-numeric: tabular-nums;
}

/* ── Feed progress rail ── */
.imm-progress {
  flex: 0 0 auto;
  height: 3px;
  margin-inline: 16px;
  border-radius: 999px;
  background: var(--imm-field-bg, rgba(255, 255, 255, 0.07));
  overflow: hidden;
}

.imm-progress-fill {
  height: 100%;
  width: 100%;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    var(--imm-accent, #60a5fa) 0%,
    var(--imm-accent, #3b82f6) 100%
  );
  opacity: 0.9;
  /* RTL feed: fill grows from the physical RIGHT edge. */
  transform-origin: 100% 50%;
  transition: transform 280ms cubic-bezier(0.16, 1, 0.3, 1);
}

/* ── Surface + finger-follow track ── */
.immersive-surface {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.imm-track {
  position: absolute;
  inset: 0;
  will-change: transform;
}

/* Commit / spring-back settle — duration MUST match SETTLE_MS in
   useImmersiveSwipe.js. While dragging there is no transition: 1:1 follow. */
.imm-track.is-settling {
  transition: transform 280ms cubic-bezier(0.16, 1, 0.3, 1);
}

/* Three stacked panes: prev above, current centered, next below. */
.imm-track-pane {
  position: absolute;
  inset-inline: 0;
  height: 100%;
}

.imm-track-pane.pane-prev {
  top: -100%;
}

.imm-track-pane.pane-current {
  top: 0;
}

.imm-track-pane.pane-next {
  top: 100%;
}

/* ── Nav chevrons (inline-start edge, vertically centered) ── */
.imm-nav {
  position: absolute;
  inset-inline-start: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.imm-nav-btn {
  width: var(--imm-nav-size, 44px);
  height: var(--imm-nav-size, 44px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--imm-glass-border, rgba(255, 255, 255, 0.18));
  background: var(--imm-glass-bg, rgba(15, 23, 42, 0.55));
  color: var(--imm-icon, #ffffff);
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition:
    background 0.15s ease,
    transform 0.1s ease,
    box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.imm-nav-btn:active {
  background: var(--imm-glass-active, rgba(255, 255, 255, 0.2));
  transform: scale(0.9);
}

/* Sub-category double chevrons: hidden keeps the space reserved so the single
   chevrons never shift when the arrows appear/disappear between panes. */
.imm-nav-btn-sub.is-hidden {
  visibility: hidden;
  pointer-events: none;
}

.imm-nav-btn svg {
  width: 24px;
  height: 24px;
}

/* ── Floating cart button (bottom corner; slightly larger than the nav) ── */
.imm-cart-btn {
  position: absolute;
  inset-inline-start: 10px;
  bottom: calc(14px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  width: calc(var(--imm-nav-size, 44px) + 8px);
  height: calc(var(--imm-nav-size, 44px) + 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--imm-glass-border, rgba(255, 255, 255, 0.18));
  background: var(--imm-glass-bg, rgba(15, 23, 42, 0.55));
  color: var(--imm-icon, #ffffff);
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition:
    background 0.15s ease,
    transform 0.1s ease,
    box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.imm-cart-btn:active {
  background: var(--imm-glass-active, rgba(255, 255, 255, 0.2));
  transform: scale(0.9);
}

.imm-cart-btn svg {
  width: 26px;
  height: 26px;
}

/* SKU-count badge — same red identity as the app-wide cart bubble */
.imm-cart-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: 2px solid var(--imm-bg-base, #0f172a);
  font-size: 0.72rem;
  font-weight: 800;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
}

.imm-cart-badge-enter-active {
  animation: imm-cart-badge-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.imm-cart-badge-leave-active {
  animation: imm-cart-badge-in 0.2s ease-out reverse;
}

@keyframes imm-cart-badge-in {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Add-to-cart bump: quick lift + tilt, spring settle. Twin keyframes so
   swapping the a/b class restarts the animation on consecutive adds. */
.imm-cart-btn.cart-bump-a {
  animation: imm-cart-bump-a 0.55s cubic-bezier(0.16, 1, 0.3, 1);
}

.imm-cart-btn.cart-bump-b {
  animation: imm-cart-bump-b 0.55s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes imm-cart-bump-a {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.16) rotate(-6deg);
  }
  60% {
    transform: scale(0.96) rotate(3deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
  }
}

@keyframes imm-cart-bump-b {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.16) rotate(-6deg);
  }
  60% {
    transform: scale(0.96) rotate(3deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
  }
}

/* Badge pops along with the bump */
.imm-cart-btn.cart-bump-a .imm-cart-badge {
  animation: imm-cart-badge-pop-a 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.imm-cart-btn.cart-bump-b .imm-cart-badge {
  animation: imm-cart-badge-pop-b 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes imm-cart-badge-pop-a {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.35);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes imm-cart-badge-pop-b {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.35);
  }
  100% {
    transform: scale(1);
  }
}

/* Expanding pulse ring (re-created per add via :key) */
.imm-cart-ring {
  position: absolute;
  inset: -2px;
  border-radius: 50%;
  border: 2px solid var(--imm-accent, #3b82f6);
  opacity: 0;
  pointer-events: none;
  animation: imm-cart-ring 0.7s ease-out;
}

@keyframes imm-cart-ring {
  0% {
    opacity: 0.9;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1.9);
  }
}

/* ── Next-step hint toast (glass pill, promo identity, no layout impact).
   Top of the screen, just below the topbar (56/60/64px) + progress rail. ── */
.imm-toast {
  position: absolute;
  top: calc(74px + env(safe-area-inset-top, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 8;
  max-width: min(86%, 420px);
  padding: 10px 18px;
  border-radius: 999px;
  border: 1px solid rgba(239, 68, 68, 0.45);
  background: var(--imm-glass-bg, rgba(15, 23, 42, 0.72));
  color: #f87171;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  pointer-events: none;
}

.imm-toast.is-gold {
  border-color: rgba(245, 158, 11, 0.45);
  color: #fbbf24;
}

.imm-toast-enter-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.imm-toast-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.imm-toast-enter-from,
.imm-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-12px);
}

/* PC pointer polish: glass buttons lift on hover. */
@media (hover: hover) {
  .imm-nav-btn:hover,
  .imm-cart-btn:hover {
    background: var(--imm-glass-active, rgba(255, 255, 255, 0.16));
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
    transform: scale(1.06);
  }

  .imm-dept-pill:hover {
    background: var(--imm-glass-active, rgba(255, 255, 255, 0.16));
  }

  .imm-sheet-item:hover {
    background: var(--imm-field-bg, rgba(255, 255, 255, 0.08));
  }
}

/* ── Overlay enter/leave ── */
.imm-overlay-enter-active,
.imm-overlay-leave-active {
  transition: opacity 0.25s ease;
}

.imm-overlay-enter-from,
.imm-overlay-leave-to {
  opacity: 0;
}

/* ── Intro splash ── */
.imm-intro {
  position: absolute;
  inset: 0;
  z-index: 7;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-inline: 24px;
  pointer-events: none;
}

.imm-intro-text {
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-align: center;
  color: var(--imm-text, #ffffff);
  text-shadow: var(--imm-name-shadow, 0 2px 18px rgba(0, 0, 0, 0.6));
}

.imm-intro-enter-active {
  transition: opacity 0.22s ease;
}
.imm-intro-leave-active {
  transition: opacity 0.32s ease;
}
.imm-intro-enter-from,
.imm-intro-leave-to {
  opacity: 0;
}

/* ── Swipe hint ── */
.imm-swipe-hint {
  position: absolute;
  inset-inline: 0;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  pointer-events: none;
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.9));
}

.imm-hint-chevron {
  width: 26px;
  height: 26px;
  color: var(--imm-accent, #60a5fa);
  filter: drop-shadow(0 0 8px rgba(96, 165, 250, 0.55));
  animation: imm-hint-bob 1.1s ease-in-out infinite;
}

.imm-hint-text {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid var(--imm-pill-border, transparent);
  background: var(--imm-pill-bg, rgba(15, 23, 42, 0.6));
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

@keyframes imm-hint-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

.imm-hint-enter-active,
.imm-hint-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.imm-hint-enter-from,
.imm-hint-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* ── Department sheet ── */
.imm-sheet-backdrop {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.imm-sheet {
  width: 100%;
  max-width: 480px;
  max-height: 70dvh;
  display: flex;
  flex-direction: column;
  background: var(--imm-bg-base, #0f172a);
  border-radius: 20px 20px 0 0;
  border-top: 1px solid var(--imm-glass-border, rgba(255, 255, 255, 0.12));
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.imm-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 10px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--imm-text, #ffffff);
}

.imm-sheet-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: var(--imm-field-bg, rgba(255, 255, 255, 0.08));
  color: var(--imm-text, #ffffff);
  cursor: pointer;
}

.imm-sheet-close svg {
  width: 18px;
  height: 18px;
}

.imm-sheet-list {
  overflow-y: auto;
  padding: 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.imm-sheet-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.85));
  font-size: 0.9rem;
  font-weight: 600;
  text-align: start;
  cursor: pointer;
  transition: background 0.15s ease;
}

.imm-sheet-item:active {
  background: var(--imm-field-bg, rgba(255, 255, 255, 0.08));
}

.imm-sheet-item.active {
  background: var(--imm-step-active-bg, rgba(59, 130, 246, 0.22));
  color: var(--imm-text, #ffffff);
}

.imm-sheet-item-count {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.55));
  font-variant-numeric: tabular-nums;
}

/* Current-category badge — grid tiles only (tablet/PC); the mobile list keeps
   its row highlight. */
.imm-sheet-item-check {
  display: none;
}

/* ── Tablet+ (≥768): the bottom-sheet list becomes a category GRID —
   full-screen takeover on tablet; PC caps it to a large centered panel. ── */
@media (min-width: 768px) {
  .imm-sheet-backdrop {
    align-items: stretch;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }

  .imm-sheet {
    max-width: none;
    max-height: none;
    border-radius: 0;
    border-top: none;
  }

  .imm-sheet-header {
    padding: 22px 28px 16px;
    font-size: 1.25rem;
    font-weight: 800;
  }

  .imm-sheet-close {
    width: 40px;
    height: 40px;
  }

  .imm-sheet-list {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 12px;
    align-content: start;
    width: 100%;
    max-width: 1200px;
    margin-inline: auto;
    padding: 4px 28px 28px;
  }

  .imm-sheet-item {
    position: relative;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    min-height: 96px;
    padding: 16px 12px;
    border-radius: 18px;
    border: 1.5px solid var(--imm-field-border, rgba(255, 255, 255, 0.12));
    background: var(--imm-field-bg, rgba(255, 255, 255, 0.05));
    text-align: center;
    transition:
      border-color 0.15s ease,
      background 0.15s ease,
      box-shadow 0.15s ease,
      transform 0.15s ease;
  }

  .imm-sheet-item-name {
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.3;
  }

  .imm-sheet-item-count {
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--imm-field-bg, rgba(255, 255, 255, 0.07));
    border: 1px solid var(--imm-field-border, rgba(255, 255, 255, 0.1));
  }

  .imm-sheet-item-count::after {
    content: ' פריטים';
  }

  /* Current category — accent ring + tint + glow + corner check badge */
  .imm-sheet-item.active {
    border-color: var(--imm-accent, #3b82f6);
    background: var(--imm-step-active-bg, rgba(59, 130, 246, 0.18));
    box-shadow: 0 0 24px rgba(59, 130, 246, 0.25);
  }

  .imm-sheet-item-check {
    position: absolute;
    top: 8px;
    inset-inline-end: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--imm-accent, #3b82f6);
    color: #ffffff;
  }

  .imm-sheet-item-check svg {
    width: 12px;
    height: 12px;
  }

  /* Promotions tile — promo-red identity */
  .imm-sheet-item.is-promo {
    border-color: rgba(239, 68, 68, 0.35);
  }

  .imm-sheet-item.is-promo .imm-sheet-item-name {
    color: #f87171;
  }

  .imm-sheet-item.is-promo.active {
    border-color: #ef4444;
    background: rgba(239, 68, 68, 0.14);
    box-shadow: 0 0 24px rgba(239, 68, 68, 0.28);
  }

  .imm-sheet-item.is-promo .imm-sheet-item-check {
    background: #ef4444;
  }
}

@media (hover: hover) and (min-width: 768px) {
  .imm-sheet-item:hover {
    transform: translateY(-2px);
    border-color: var(--imm-glass-border, rgba(255, 255, 255, 0.25));
    background: var(--imm-glass-active, rgba(255, 255, 255, 0.1));
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.25);
  }

  .imm-sheet-item.is-promo:hover {
    border-color: rgba(239, 68, 68, 0.6);
  }
}

/* ── PC (≥1280): large centered panel instead of full screen ── */
@media (min-width: 1280px) {
  .imm-sheet-backdrop {
    align-items: center;
    padding: 32px;
  }

  .imm-sheet {
    max-width: min(1160px, 94vw);
    max-height: 86dvh;
    border-radius: 24px;
    border: 1px solid var(--imm-glass-border, rgba(255, 255, 255, 0.12));
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
  }
}

.imm-sheet-enter-active,
.imm-sheet-leave-active {
  transition: opacity 0.25s ease;
}

.imm-sheet-enter-active .imm-sheet,
.imm-sheet-leave-active .imm-sheet {
  transition: transform 0.25s ease;
}

.imm-sheet-enter-from,
.imm-sheet-leave-to {
  opacity: 0;
}

.imm-sheet-enter-from .imm-sheet,
.imm-sheet-leave-to .imm-sheet {
  transform: translateY(100%);
}

/* Tablet/PC: the grid takeover scale-fades instead of sliding up (must come
   AFTER the base rule — same specificity, source order decides). */
@media (min-width: 768px) {
  .imm-sheet-enter-from .imm-sheet,
  .imm-sheet-leave-to .imm-sheet {
    transform: scale(0.96);
  }
}

/* ── Onboarding fade ── */
.imm-onb-enter-active,
.imm-onb-leave-active {
  transition: opacity 0.25s ease;
}

.imm-onb-enter-from,
.imm-onb-leave-to {
  opacity: 0;
}

/* ── Promo items modal (backdrop fade + sheet rise) ── */
.imm-pm-enter-active,
.imm-pm-leave-active {
  transition: opacity 0.25s ease;
}

.imm-pm-enter-active :deep(.imm-pm-sheet),
.imm-pm-leave-active :deep(.imm-pm-sheet) {
  transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
}

.imm-pm-enter-from,
.imm-pm-leave-to {
  opacity: 0;
}

.imm-pm-enter-from :deep(.imm-pm-sheet),
.imm-pm-leave-to :deep(.imm-pm-sheet) {
  transform: translateY(8%);
}

/* ════════════════════════════════════════════════════════════════════════
   RESPONSIVE TOKENS — the pane's grid personalities live in ImmersivePane.vue;
   these blocks only scale the shared size tokens per breakpoint.
   ════════════════════════════════════════════════════════════════════════ */

/* ── Tablet (768–1279): wider frame, bigger touch controls. ── */
@media (min-width: 768px) and (max-width: 1279px) {
  .immersive-overlay {
    --imm-price-size: 3.2rem;
    --imm-orig-size: 1.1rem;
    --imm-name-size: 1.5rem;
    --imm-ident-size: 0.9rem;
    --imm-total-size: 1rem;
    --imm-step-size: 72px;
    --imm-step-font: 1.9rem;
    --imm-qty-w: 84px;
    --imm-trash-size: 62px;
    --imm-img-max: 52vh;
    --imm-disc-w: 92px;
    --imm-disc-gutter: 100px;
    --imm-disc-dot: 14px;
    --imm-disc-qty-font: 0.72rem;
    --imm-disc-price-font: 0.84rem;
    --imm-disc-caption: 0.7rem;
    --imm-nav-size: 52px;
    --imm-corner-size: 46px;
    --imm-corner-reserve: 130px;
    --imm-dept-size: 0.9rem;
  }

  /* Widen toward the device edges (never below today's 760) to use the room. */
  .immersive-frame {
    max-width: clamp(760px, 94vw, 920px);
  }

  .imm-topbar {
    min-height: 60px;
  }
}

/* ── PC (≥1280): full-width editorial split (pane layout in ImmersivePane). ── */
@media (min-width: 1280px) {
  .immersive-overlay {
    --imm-price-size: 3.4rem;
    --imm-orig-size: 1.25rem;
    --imm-name-size: 1.65rem;
    --imm-ident-size: 1.02rem;
    --imm-total-size: 1.02rem;
    --imm-step-size: 68px;
    --imm-step-font: 1.9rem;
    --imm-qty-w: 82px;
    --imm-trash-size: 60px;
    --imm-img-max: 74vh;
    /* PC rail is a showpiece column: thick rail line, big dots, roomy labels. */
    --imm-disc-w: 180px;
    --imm-disc-rail-w: 10px;
    --imm-disc-dot: 20px;
    --imm-disc-qty-font: 0.85rem;
    --imm-disc-price-font: 1.05rem;
    --imm-disc-caption: 0.9rem;
    --imm-nav-size: 56px;
    --imm-corner-size: 50px;
    --imm-corner-reserve: 140px;
    --imm-dept-size: 0.98rem;
  }

  .immersive-frame {
    max-width: min(1600px, 96vw);
  }

  .imm-topbar {
    min-height: 64px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .immersive-overlay,
  .immersive-overlay::before,
  .immersive-overlay::after,
  .immersive-overlay.theme-switching,
  .immersive-overlay.theme-switching :deep(*),
  .imm-overlay-enter-active,
  .imm-overlay-leave-active,
  .imm-track.is-settling,
  .imm-progress-fill,
  .imm-intro-enter-active,
  .imm-intro-leave-active,
  .imm-hint-enter-active,
  .imm-hint-leave-active,
  .imm-sheet-enter-active,
  .imm-sheet-leave-active,
  .imm-sheet-enter-active .imm-sheet,
  .imm-sheet-leave-active .imm-sheet,
  .imm-onb-enter-active,
  .imm-onb-leave-active,
  .imm-pm-enter-active,
  .imm-pm-leave-active,
  .imm-pm-enter-active :deep(.imm-pm-sheet),
  .imm-pm-leave-active :deep(.imm-pm-sheet),
  .imm-toast-enter-active,
  .imm-toast-leave-active,
  .imm-dept-pill,
  .imm-nav-btn,
  .imm-cart-btn {
    transition: none;
  }

  .imm-hint-chevron,
  .imm-cart-btn.cart-bump-a,
  .imm-cart-btn.cart-bump-b,
  .imm-cart-btn.cart-bump-a .imm-cart-badge,
  .imm-cart-btn.cart-bump-b .imm-cart-badge,
  .imm-cart-badge-enter-active,
  .imm-cart-badge-leave-active,
  .imm-cart-ring {
    animation: none;
  }
}
</style>
